'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useSupabase } from '@/hooks/use-supabase'
import { useAuth } from '@/components/auth/auth-provider'

export type SubscriptionInfo = {
    active: boolean
    lifetime: boolean
    plan: 'plus' | 'pro' | 'free' | null
    status: string | null
    trialActive: boolean
    trialDaysLeft: number
    period_start: string | null
    period_end: string | null
    billing_cycle: 'monthly' | 'annual' | null
    price_cents?: number | null
    currency?: string | null
}

interface SubscriptionContextType {
    loading: boolean
    data: SubscriptionInfo | null
    refetch: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

export function SubscriptionProvider({ children }: { children: ReactNode }) {
    const supabase = useSupabase()
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState<SubscriptionInfo | null>(null)

    const load = async () => {
        if (!user) { setData(null); setLoading(false); return }
        // Don't set loading to true on refetch to avoid flickering
        // setLoading(true) 
        try {
            const { data: sub } = await supabase
                .from('user_subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .maybeSingle()

            const now = new Date()
            const lifetime = !!sub?.lifetime
            const periodEnd = sub?.period_end ? new Date(sub.period_end) : null
            const trialEnd = sub?.trial_end ? new Date(sub.trial_end) : null
            const trialActive = !!trialEnd && trialEnd >= now
            // Require a valid period_end in the future for paid active (unless lifetime)
            const paidActive = sub?.status === 'active' && !!periodEnd && periodEnd >= now && !!sub?.wompi_transaction_id
            const active = lifetime || paidActive || trialActive

            let trialDaysLeft = 0
            if (trialActive && trialEnd) {
                const diffDays = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                // Inclusivo: si el trial está activo, mostrar al menos 1 día
                trialDaysLeft = Math.max(1, diffDays)
            }

            // Determine plan: if no subscription or inactive, default to 'free'
            let effectivePlan: 'plus' | 'pro' | 'free' | null = (sub?.plan as any) ?? null
            if (!active && !trialActive) {
                effectivePlan = 'free'
            }

            setData({
                active,
                lifetime,
                plan: effectivePlan,
                status: (sub?.status as any) ?? null,
                trialActive,
                trialDaysLeft,
                period_start: sub?.period_start ?? null,
                period_end: sub?.period_end ?? null,
                billing_cycle: (sub?.billing_cycle as any) ?? null,
                price_cents: (sub as any)?.price_cents ?? null,
                currency: (sub as any)?.currency ?? null,
            })
        } catch (error) {
            console.error('Error loading subscription:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)
        load()
    }, [user?.id])

    return (
        <SubscriptionContext.Provider value={{ loading, data, refetch: load }}>
            {children}
        </SubscriptionContext.Provider>
    )
}

export function useSubscriptionContext() {
    const context = useContext(SubscriptionContext)
    if (context === undefined) {
        throw new Error('useSubscriptionContext must be used within a SubscriptionProvider')
    }
    return context
}
